/**
 * QA Automation admin page.
 *
 * Only visible to org admins of orgs whose
 * `settings.features.qaAutomation === true`, plus super-admins everywhere.
 *
 * Reuses the existing ShadCN component vocabulary (Card / ResponsiveTable /
 * Tabs / Modal / Badge / Button / EmptyState) — see docs/guides/ui/UI_GUIDE.md
 * for the patterns. The layout is QA-specific (Coverage banner → Tabs across
 * Tests / Batches / Runs / Logs) but every primitive on the page is a
 * pre-existing app component.
 *
 * See plans/qa-automation-tenant-and-admin-page.md.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileText,
  History,
  Layers,
  Loader2,
  PlayCircle,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Modal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Column,
} from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { ExecutionLogViewer } from '@/components/execution/ExecutionLogViewer';
import { useExecutionLogs } from '@/hooks/useExecutionLogs';
import { formatDuration, formatRelativeTime } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  generateTestFlows,
  getStatus,
  runAll,
  type QaBatch,
  type QaCellStatus,
  type QaPrimitiveStatus,
  type QaStatus,
} from '@/services/qaAutomation';

// Auto-refresh cadence while a batch is in-flight or recently kicked.
const POLL_MS = 10_000;

// One row per (primitive × cell) — the unit a tester actually thinks about.
interface TestRow {
  id: string;
  primitiveId: string;
  primitiveName: string;
  catalogueVisibility: QaPrimitiveStatus['catalogueVisibility'];
  flowId: string;
  cellName: string;
  testConfigId: string;
  capabilities: string[];
  forcedRat: string | null;
  forcedNetworkMccMnc: string | null;
  assertionNodeCount: number;
  hasSentinelOnly: boolean;
  cell: QaCellStatus;
}

// Tab id the runs view subscribes to. Mirrors TabsTrigger value.
const TAB_RUNS = 'runs';

export default function QaAutomationPage() {
  const { activeOrganization } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();
  const fmt = useDateFormat();
  const orgId = activeOrganization?.id;

  const [status, setStatus] = useState<QaStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [logsExecutionId, setLogsExecutionId] = useState<string | null>(null);
  const [logsTestLabel, setLogsTestLabel] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('tests');
  const [batchFilter, setBatchFilter] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!orgId) {
      setStatusError('No active organization. Switch to the QA Validations tenant first.');
      setStatusLoading(false);
      return;
    }
    try {
      setStatusError(null);
      const s = await getStatus(orgId);
      setStatus(s);
    } catch (e: any) {
      setStatusError(e?.message || 'Failed to load status');
    } finally {
      setStatusLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Auto-poll when there's a recent running batch.
  const hasRunningWork = useMemo(
    () => (status?.batches || []).some((b) => b.running > 0),
    [status],
  );

  useEffect(() => {
    if (!hasRunningWork) return;
    const id = setInterval(() => refreshStatus(), POLL_MS);
    return () => clearInterval(id);
  }, [hasRunningWork, refreshStatus]);

  const onGenerate = async () => {
    if (!orgId) return;
    setGenerating(true);
    try {
      const r = await generateTestFlows(orgId);
      toastSuccess(
        `Flows: +${r.summary.created.flows} / ~${r.summary.updated.flows} / =${r.summary.unchanged.flows}. ` +
          `Configs: +${r.summary.created.configs} / ~${r.summary.updated.configs} / =${r.summary.unchanged.configs}` +
          (r.summary.removed.configs ? ` / -${r.summary.removed.configs}` : ''),
        'Generator complete',
      );
      await refreshStatus();
    } catch (e: any) {
      toastError(e?.message || 'unknown error', 'Generator failed');
    } finally {
      setGenerating(false);
    }
  };

  const onRunAll = async () => {
    if (!orgId) return;
    setRunning(true);
    try {
      const r = await runAll(orgId);
      toastSuccess(`${r.runs.length} run(s) queued (batch ${r.batchId})`, 'Batch started');
      await refreshStatus();
    } catch (e: any) {
      toastError(e?.message || 'unknown error', 'Run All failed');
    } finally {
      setRunning(false);
    }
  };

  // Flatten primitives × cells into one row per (primitive, cell).
  const testRows: TestRow[] = useMemo(() => {
    if (!status) return [];
    const rows: TestRow[] = [];
    for (const p of status.primitives) {
      for (const c of p.cells) {
        rows.push({
          id: `${p.primitiveId}::${c.name}`,
          primitiveId: p.primitiveId,
          primitiveName: p.primitiveName,
          catalogueVisibility: p.catalogueVisibility,
          flowId: p.flowId,
          cellName: c.name,
          testConfigId: c.testConfigId,
          capabilities: c.allocationCapabilities,
          forcedRat: c.forcedRat,
          forcedNetworkMccMnc: c.forcedNetworkMccMnc,
          assertionNodeCount: p.assertionNodeCount,
          hasSentinelOnly: p.hasSentinelOnly,
          cell: c,
        });
      }
    }
    return rows;
  }, [status]);

  // Recent runs derived from the same per-cell payload — sorted desc by lastRunAt.
  // Optionally filtered to a single batch (set by clicking a row in the Batches tab).
  const recentRuns = useMemo(
    () =>
      testRows
        .filter((r) => r.cell.lastRunId)
        .filter((r) => (batchFilter ? r.cell.lastBatchId === batchFilter : true))
        .sort((a, b) => {
          const ta = a.cell.lastRunAt ? new Date(a.cell.lastRunAt).getTime() : 0;
          const tb = b.cell.lastRunAt ? new Date(b.cell.lastRunAt).getTime() : 0;
          return tb - ta;
        }),
    [testRows, batchFilter],
  );

  // ─── Columns ─────────────────────────────────────────────────────────────

  const testsColumns: Column<TestRow>[] = useMemo(
    () => [
      {
        key: 'primitiveName',
        header: 'Primitive',
        sortable: true,
        render: (_v, row) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.primitiveName}</div>
            <div className="font-mono text-xs text-muted-foreground truncate">{row.primitiveId}</div>
          </div>
        ),
      },
      {
        key: 'cellName',
        header: 'Cell',
        sortable: true,
        render: (_v, row) => <span className="font-mono text-xs">{row.cellName}</span>,
      },
      {
        key: 'catalogueVisibility',
        header: 'Visibility',
        sortable: true,
        render: (_v, row) => (
          <Badge variant="outline" className="capitalize">
            {row.catalogueVisibility}
          </Badge>
        ),
      },
      {
        key: 'capabilities',
        header: 'Constraints',
        render: (_v, row) => (
          <div className="flex flex-wrap gap-1 text-xs">
            {row.capabilities.length === 0 && !row.forcedRat && !row.forcedNetworkMccMnc && (
              <span className="text-muted-foreground">—</span>
            )}
            {row.capabilities.map((c) => (
              <Badge key={c} variant="secondary" className="font-mono text-[10px]">
                {c}
              </Badge>
            ))}
            {row.forcedRat && (
              <Badge variant="outline" className="font-mono text-[10px]">
                rat={row.forcedRat}
              </Badge>
            )}
            {row.forcedNetworkMccMnc && (
              <Badge variant="outline" className="font-mono text-[10px]">
                plmn={row.forcedNetworkMccMnc}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'assertionNodeCount',
        header: 'Assertions',
        sortable: true,
        render: (_v, row) =>
          row.hasSentinelOnly ? (
            <Badge variant="secondary">sentinel</Badge>
          ) : (
            <span className="tabular-nums">{row.assertionNodeCount}</span>
          ),
      },
      {
        key: 'lastRunStatus',
        header: 'Last status',
        render: (_v, row) => <StatusChip status={row.cell.lastRunStatus} />,
      },
      {
        key: 'lastRunAt',
        header: 'Last run',
        sortable: true,
        render: (_v, row) =>
          row.cell.lastRunAt ? (
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(row.cell.lastRunAt)}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">never</span>
          ),
      },
      {
        key: 'lastRunDurationMs',
        header: 'Duration',
        render: (_v, row) =>
          row.cell.lastRunDurationMs ? (
            <span className="text-xs tabular-nums">
              {formatDuration(row.cell.lastRunDurationMs)}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (_v, row) => (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={!row.cell.lastExecutionId}
              onClick={(e) => {
                e.stopPropagation();
                if (!row.cell.lastExecutionId) return;
                setLogsExecutionId(row.cell.lastExecutionId);
                setLogsTestLabel(`${row.primitiveName} · ${row.cellName}`);
              }}
              title={row.cell.lastExecutionId ? 'View logs' : 'No run yet'}
            >
              <FileText className="h-4 w-4" />
            </Button>
            <Link
              to={`/flows/${row.flowId}`}
              onClick={(e) => e.stopPropagation()}
              title="Open flow"
            >
              <Button size="sm" variant="ghost">
                <Layers className="h-4 w-4" />
              </Button>
            </Link>
            {row.cell.lastExecutionId && (
              <Link
                to={`/reports/execution/${row.cell.lastExecutionId}`}
                onClick={(e) => e.stopPropagation()}
                title="Open report"
              >
                <Button size="sm" variant="ghost">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  const batchesColumns: Column<QaBatch>[] = useMemo(
    () => [
      {
        key: 'batchId',
        header: 'Batch',
        render: (_v, row) => <span className="font-mono text-xs">{row.batchId}</span>,
      },
      {
        key: 'startedAt',
        header: 'Started',
        sortable: true,
        render: (_v, row) => (
          <div className="flex flex-col">
            <span className="text-sm">{fmt.formatDateTime(row.startedAt)}</span>
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(row.startedAt)}
            </span>
          </div>
        ),
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        render: (_v, row) => <span className="tabular-nums">{row.total}</span>,
      },
      {
        key: 'pass',
        header: 'Pass',
        sortable: true,
        render: (_v, row) =>
          row.pass > 0 ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 tabular-nums">
              {row.pass}
            </Badge>
          ) : (
            <span className="text-muted-foreground tabular-nums">0</span>
          ),
      },
      {
        key: 'fail',
        header: 'Fail',
        sortable: true,
        render: (_v, row) =>
          row.fail > 0 ? (
            <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 tabular-nums">
              {row.fail}
            </Badge>
          ) : (
            <span className="text-muted-foreground tabular-nums">0</span>
          ),
      },
      {
        key: 'running',
        header: 'Running',
        sortable: true,
        render: (_v, row) =>
          row.running > 0 ? (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 tabular-nums">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              {row.running}
            </Badge>
          ) : (
            <span className="text-muted-foreground tabular-nums">0</span>
          ),
      },
    ],
    [fmt],
  );

  const runsColumns: Column<TestRow>[] = useMemo(
    () => [
      {
        key: 'primitiveName',
        header: 'Test',
        sortable: true,
        render: (_v, row) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.primitiveName}</div>
            <div className="font-mono text-xs text-muted-foreground truncate">
              {row.cellName}
            </div>
          </div>
        ),
      },
      {
        key: 'lastRunStatus',
        header: 'Status',
        sortable: true,
        render: (_v, row) => <StatusChip status={row.cell.lastRunStatus} />,
      },
      {
        key: 'lastRunAt',
        header: 'When',
        sortable: true,
        render: (_v, row) => (
          <span className="text-xs text-muted-foreground">
            {row.cell.lastRunAt ? formatRelativeTime(row.cell.lastRunAt) : 'never'}
          </span>
        ),
      },
      {
        key: 'lastRunDurationMs',
        header: 'Duration',
        render: (_v, row) =>
          row.cell.lastRunDurationMs ? (
            <span className="text-xs tabular-nums">
              {formatDuration(row.cell.lastRunDurationMs)}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: 'lastBatchId',
        header: 'Batch',
        render: (_v, row) =>
          row.cell.lastBatchId ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setBatchFilter(row.cell.lastBatchId);
              }}
              className="font-mono text-[10px] text-muted-foreground hover:text-foreground hover:underline"
              title="Filter to this batch"
            >
              {row.cell.lastBatchId}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: 'lastRunError',
        header: 'Error',
        render: (_v, row) =>
          row.cell.lastRunError ? (
            <span
              className="text-xs text-rose-700 truncate max-w-md inline-block"
              title={row.cell.lastRunError}
            >
              {row.cell.lastRunError}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (_v, row) => (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={!row.cell.lastExecutionId}
              onClick={(e) => {
                e.stopPropagation();
                if (!row.cell.lastExecutionId) return;
                setLogsExecutionId(row.cell.lastExecutionId);
                setLogsTestLabel(`${row.primitiveName} · ${row.cellName}`);
              }}
              title="View logs"
            >
              <FileText className="h-4 w-4" />
            </Button>
            {row.cell.lastExecutionId && (
              <Link
                to={`/reports/execution/${row.cell.lastExecutionId}`}
                onClick={(e) => e.stopPropagation()}
                title="Open report"
              >
                <Button size="sm" variant="ghost">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AppShell>
      <div className="container max-w-7xl py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">QA Automation</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Tenant:{' '}
              <span className="font-medium text-foreground">
                {activeOrganization?.name ?? '—'}
              </span>
              {' · '}
              <Badge variant="secondary" className="ml-1">
                qaAutomation
              </Badge>
            </p>
            <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
              Generates one test flow per primitive (across catalogue + internal + e2e tiers) and
              runs them via the standard test execution path. Each test is a real{' '}
              <Link to="/flows" className="underline">
                Flow
              </Link>{' '}
              in this tenant with assertion nodes baked in from{' '}
              <code className="text-xs">
                apps/frontend-new/functions/src/qa/tests/&lt;primitiveId&gt;.ts
              </code>
              .
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={refreshStatus} variant="outline" size="sm" disabled={statusLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${statusLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={onGenerate} disabled={generating || statusLoading} variant="outline">
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Test Flows
                </>
              )}
            </Button>
            <Button onClick={onRunAll} disabled={running || statusLoading} variant="default">
              {running ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting…
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Run All
                </>
              )}
            </Button>
          </div>
        </div>

        {statusError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not load status</AlertTitle>
            <AlertDescription>{statusError}</AlertDescription>
          </Alert>
        )}

        {/* Coverage stats — Card grid */}
        <CoverageGrid status={status} loading={statusLoading} />

        {/* Tabs: Tests / Batches / Recent Runs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="tests">
              <Layers className="h-4 w-4 mr-2" />
              Tests
              {status && (
                <Badge variant="secondary" className="ml-2">
                  {testRows.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="batches">
              <Activity className="h-4 w-4 mr-2" />
              Batches
              {status?.batches?.length ? (
                <Badge variant="secondary" className="ml-2">
                  {status.batches.length}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="runs">
              <History className="h-4 w-4 mr-2" />
              Recent runs
              {recentRuns.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {recentRuns.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TESTS */}
          <TabsContent value="tests" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Tests ({testRows.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {testRows.length === 0 && !statusLoading ? (
                  <EmptyState
                    icon={<Layers className="h-10 w-10" />}
                    title="No test flows generated yet"
                    description={'Click "Generate Test Flows" to scaffold one Flow per primitive plus one TestConfig per (primitive × cell).'}
                  />
                ) : (
                  <ResponsiveTable<TestRow>
                    columns={testsColumns}
                    data={testRows}
                    keyExtractor={(r) => r.id}
                    searchable
                    searchPlaceholder="Search primitives, cells, capabilities…"
                    paginated
                    defaultPageSize={25}
                    loading={statusLoading}
                    emptyMessage="No tests"
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BATCHES */}
          <TabsContent value="batches" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Batches ({status?.batches?.length ?? 0})</CardTitle>
              </CardHeader>
              <CardContent>
                {(!status?.batches || status.batches.length === 0) && !statusLoading ? (
                  <EmptyState
                    icon={<Activity className="h-10 w-10" />}
                    title="No batches yet"
                    description={'Click "Run All" to fire every TestConfig in one batch and watch results land here in real time.'}
                  />
                ) : (
                  <ResponsiveTable<QaBatch>
                    columns={batchesColumns}
                    data={status?.batches ?? []}
                    keyExtractor={(b) => b.batchId}
                    paginated
                    defaultPageSize={10}
                    loading={statusLoading}
                    emptyMessage="No batches"
                    onRowClick={(b) => {
                      setBatchFilter(b.batchId);
                      setActiveTab(TAB_RUNS);
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECENT RUNS */}
          <TabsContent value="runs" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle>Recent runs ({recentRuns.length})</CardTitle>
                  {batchFilter && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">Filtered by batch:</span>
                      <Badge variant="secondary" className="font-mono">
                        {batchFilter}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBatchFilter(null)}
                      >
                        Clear filter
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {recentRuns.length === 0 && !statusLoading ? (
                  <EmptyState
                    icon={<History className="h-10 w-10" />}
                    title={batchFilter ? 'No runs match this filter' : 'No runs yet'}
                    description={
                      batchFilter
                        ? `No QA runs are tagged with batch ${batchFilter}. Clear the filter to see all runs.`
                        : 'Once you click "Run All", individual test executions will appear here with their pass/fail status and logs.'
                    }
                  />
                ) : (
                  <ResponsiveTable<TestRow>
                    columns={runsColumns}
                    data={recentRuns}
                    keyExtractor={(r) => r.id}
                    searchable
                    searchPlaceholder="Search runs…"
                    paginated
                    defaultPageSize={25}
                    loading={statusLoading}
                    emptyMessage="No runs"
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Logs Modal */}
        <LogsModal
          executionId={logsExecutionId}
          label={logsTestLabel}
          onClose={() => setLogsExecutionId(null)}
        />
      </div>
    </AppShell>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function CoverageGrid({ status, loading }: { status: QaStatus | null; loading: boolean }) {
  if (!status) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          {loading ? 'Loading coverage…' : 'No coverage data yet'}
        </CardContent>
      </Card>
    );
  }
  const c = status.coverage;
  const items: { label: string; value: number; tone?: 'emerald' | 'rose' | 'amber' | 'muted' }[] = [
    { label: 'Primitives', value: c.primitivesTotal },
    { label: 'With assertions', value: c.primitivesAuthored, tone: 'emerald' },
    { label: 'Sentinel only', value: c.primitivesSentinelOnly, tone: 'amber' },
    { label: 'Cells', value: c.cellsTotal },
    { label: 'Pass (24h)', value: c.cellsPassLast24h, tone: 'emerald' },
    { label: 'Fail (24h)', value: c.cellsFailLast24h, tone: 'rose' },
    { label: 'No run (24h)', value: c.cellsNoRunLast24h, tone: 'muted' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
      {items.map((s) => (
        <Card key={s.label}>
          <CardContent className="py-4">
            <div className="text-xs uppercase text-muted-foreground">{s.label}</div>
            <div className={`text-2xl font-semibold mt-1 ${toneClass(s.tone)}`}>{s.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function toneClass(tone?: 'emerald' | 'rose' | 'amber' | 'muted') {
  switch (tone) {
    case 'emerald':
      return 'text-emerald-700';
    case 'rose':
      return 'text-rose-700';
    case 'amber':
      return 'text-amber-700';
    case 'muted':
      return 'text-muted-foreground';
    default:
      return 'text-foreground';
  }
}

function StatusChip({ status }: { status: QaCellStatus['lastRunStatus'] }) {
  if (status === 'completed') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        <CheckCircle2 className="h-3 w-3 mr-1" /> pass
      </Badge>
    );
  }
  if (status === 'failed' || status === 'cancelled') {
    return (
      <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">
        <AlertCircle className="h-3 w-3 mr-1" /> fail
      </Badge>
    );
  }
  if (status === 'running' || status === 'pending') {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
        <Loader2 className="h-3 w-3 mr-1 animate-spin" /> running
      </Badge>
    );
  }
  return <Badge variant="outline">never</Badge>;
}

function LogsModal({
  executionId,
  label,
  onClose,
}: {
  executionId: string | null;
  label: string;
  onClose: () => void;
}) {
  const { logs, isLoading, isLive, error } = useExecutionLogs(executionId);
  return (
    <Modal
      isOpen={!!executionId}
      onClose={onClose}
      title={label || 'Execution logs'}
      description={executionId ? `execution ${executionId}` : undefined}
      size="2xl"
    >
      {isLoading && (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading logs…</div>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Could not load logs</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!isLoading && !error && (
        <ExecutionLogViewer
          logs={logs}
          isLive={isLive}
          showFilters
          showExport
          autoScroll
          maxHeight="60vh"
        />
      )}
    </Modal>
  );
}
